import { useMemo } from "react";
import { type InboxScope, useInboxScope } from "@/providers/inbox-scope";
import { useBookInsureds } from "./use-book-insureds";
import type { ItemWithInsured } from "./use-pinned-insureds";

/**
 * The only part of an insured a set scope reads. Both shapes the rail holds one of — a book list's
 * `ItemWithInsured["insured"]` and the flattened `BookInsured` — satisfy it, which is what lets the
 * per-list filter and the count in the scope row share one predicate.
 */
interface ScopedAccount {
  agent?: {
    id: string;
    firstName: string;
    lastName: string;
    agency?: { id: string; name: string } | null;
  } | null;
}

/** A scope that names a set of accounts, which is the only kind the rail filters on. */
type AccountSetScope = Extract<InboxScope, { kind: "agency" | "agent" }>;

/**
 * The scope narrowing the rail to a set of accounts, and `null` for one that does not.
 *
 * **A set scope filters the rail; the insured scope must not.** A scope that names a set of accounts
 * is a lens on the book — that is what it is for. A scope naming one account is not: a rail holding
 * a single insured is how a broker loses the ability to switch insureds from a scoped page, which is
 * the same reason `qwib-search.tsx` keeps the dropdown's insured suggestions book-wide. That
 * asymmetry is why this reads the scope's *kind* rather than "is there a scope".
 *
 * Written as a positive list rather than "anything but an insured", so a fourth kind of scope
 * arriving leaves the rail unfiltered — the same direction the insured scope takes — instead of
 * silently falling into one of the branches below.
 */
export const accountSetScope = (scope: InboxScope | null): AccountSetScope | null => {
  switch (scope?.kind) {
    case "agency":
    case "agent":
      return scope;
    default:
      return null;
  }
};

/** Whether `insured` is one of the accounts `scope` names. */
const holds = (scope: AccountSetScope, { agent }: ScopedAccount): boolean =>
  scope.kind === "agency" ? agent?.agency?.id === scope.agencyId : agent?.id === scope.agentId;

/** The scope's own name, read off an insured inside it. */
const nameOf = (scope: AccountSetScope, { agent }: ScopedAccount): string | null => {
  if (scope.kind === "agency") {
    return agent?.agency?.name || null;
  }

  return agent ? `${agent.firstName} ${agent.lastName}`.trim() || null : null;
};

/**
 * A Book list, narrowed to whatever set of accounts the route is scoped to.
 *
 * Returns the array it was given when unscoped, references and all: `useFilteredInsureds` builds a
 * microfuzz index keyed on this value, and a fresh array each render would rebuild it on every
 * keystroke. `scope` is the only dependency because `useInboxScope` holds it stable across renders.
 */
export const useScopedItems = <T>(items: ItemWithInsured<T>[]): ItemWithInsured<T>[] => {
  const scope = useInboxScope();

  return useMemo(() => {
    const setScope = accountSetScope(scope);

    return setScope ? items.filter((item) => holds(setScope, item.insured)) : items;
  }, [scope, items]);
};

/**
 * What to call a set scope the book cannot name, by kind. Shared with the search-box chip so the two
 * surfaces describing one scope cannot describe it differently.
 */
export const UNNAMED_SCOPE: Record<"agency" | "agent", string> = {
  agency: "Selected agency",
  agent: "Selected agent",
};

export interface BookScope {
  /** The kind of set the rail is filtered to, for copy that has to say which. */
  dimension: "agency" | "agent";
  /** `null` whenever the loaded book holds no account inside the scope to read a name off. */
  name: string | null;
  insuredCount: number;
}

/**
 * What the rail's filter left, for the row that has to explain it.
 *
 * Both halves come off the book rather than off a query, and that is a deliberate limit rather than
 * an oversight. The scope is an id in the route; the name behind it is only knowable here if one of
 * its accounts is in the loaded book. **69.2% of agent scopes and 56.3% of agency scopes hold
 * exactly one open account** and a scope reached from the type-ahead can hold none at all, so `name`
 * is null often enough that every caller has to have an answer for it. The alternative — a round
 * trip per chip to resolve one name — buys a name the agent card (B-1846) already fetches for the
 * agent case, on a query that exists.
 *
 * `insuredCount` is book-wide rather than per-tab on purpose: it exists to tell a broker that a rail
 * showing one row is showing one row *because of the chip*, and a count that changed with the tab
 * would answer a question they did not ask.
 */
export const useBookScope = (): BookScope | null => {
  const scope = useInboxScope();
  const bookInsureds = useBookInsureds();

  return useMemo(() => {
    const setScope = accountSetScope(scope);

    if (!setScope) {
      return null;
    }

    const scopedInsureds = bookInsureds.filter((insured) => holds(setScope, insured));
    const [firstScopedInsured] = scopedInsureds;

    return {
      dimension: setScope.kind,
      name: firstScopedInsured ? nameOf(setScope, firstScopedInsured) : null,
      insuredCount: scopedInsureds.length,
    };
  }, [scope, bookInsureds]);
};
